import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Avatar } from './Avatar.tsx'
import { outbox } from '../store/outbox.ts'
import { SavedIcon } from './SavedIcon.tsx'
import { chatListTime, messageTime } from '../lib/format.ts'
import { useChats } from '../store/chats.ts'
import { useAuth } from '../store/auth.ts'
import { builtAt } from '../lib/build.ts'
import type { ChatSummary, SearchResult } from '../api/types.ts'
import { NotificationSettings } from './NotificationSettings.tsx'
import { ProfileSettings } from './ProfileSettings.tsx'
import { FamilyPanel } from './FamilyPanel.tsx'
import { NewChatDialog } from './NewChatDialog.tsx'
import { ContactList } from './ContactList.tsx'
import { PushBanner } from './PushBanner.tsx'

/** «19 сентября, 14:07» — коротко и по-человечески. */
function buildDate(): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(builtAt))
  } catch {
    return 'неизвестно'
  }
}

export function ChatList() {
  const chats = useChats((s) => s.chats)
  const loaded = useChats((s) => s.chatsLoaded)
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const [query, setQuery] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [openSheet, setOpenSheet] = useState<'profile' | 'family' | 'new' | null>(null)
  const [found, setFound] = useState<SearchResult[] | null>(null)
  const [searching, setSearching] = useState(false)
  const searchRun = useRef(0)
  const typing = useChats((s) => s.typing)
  const search = useChats((s) => s.search)
  const pinChat = useChats((s) => s.pinChat)
  const archiveChat = useChats((s) => s.archiveChat)
  const clearChat = useChats((s) => s.clearChat)
  const removeChat = useChats((s) => s.removeChat)
  const markUnread = useChats((s) => s.markUnread)
  const muteChat = useChats((s) => s.muteChat)
  const [rowMenu, setRowMenu] = useState<{ chat: ChatSummary; x: number; y: number } | null>(null)
  const [showArchive, setShowArchive] = useState(false)

  /**
   * Какая вкладка открыта. Помним между заходами: кто ищет людей
   * по контактам, тот и в следующий раз, скорее всего, за ними.
   */
  const [tab, setTabState] = useState<'chats' | 'contacts'>(() => {
    try {
      return localStorage.getItem('family.listTab') === 'contacts' ? 'contacts' : 'chats'
    } catch {
      return 'chats'
    }
  })
  const setTab = (next: 'chats' | 'contacts') => {
    setTabState(next)
    try {
      localStorage.setItem('family.listTab', next)
    } catch {
      /* в приватном окне хранилище закрыто — просто не помним */
    }
  }

  /**
   * Поиск по сообщениям запускается не сразу: пока человек печатает,
   * каждая буква — это запрос. Четверти секунды тишины достаточно,
   * чтобы понять, что набор закончился.
   *
   * Номер запуска нужен от гонки: медленный ответ на «мол» не должен
   * затереть быстрый ответ на «молоко».
   */
  useEffect(() => {
    const text = query.trim()
    // В контактах ищем только людей, и ищем у себя: переписку
    // перебирать незачем
    if (text.length < 2 || tab === 'contacts') {
      setFound(null)
      setSearching(false)
      return
    }

    const run = ++searchRun.current
    setSearching(true)

    const timer = window.setTimeout(() => {
      search(text)
        .then((results) => {
          if (run === searchRun.current) setFound(results)
        })
        .catch(() => {
          if (run === searchRun.current) setFound([])
        })
        .finally(() => {
          if (run === searchRun.current) setSearching(false)
        })
    }, 250)

    return () => window.clearTimeout(timer)
  }, [query, search, tab])

  const archivedCount = chats.filter((c) => c.archived).length

  /**
   * Черновики и неотправленное читаются один раз на отрисовку, а не
   * по три раза на строку.
   *
   * Хранилище браузера — не бесплатное чтение: очередь приходится
   * разбирать целиком, а строк в списке десяток, и перерисовывается
   * он от каждого «печатает…», то есть раз в две секунды.
   */
  const outboxVersion = useChats((s) => s.outboxVersion)

  const pendingByChat = useMemo(() => {
    const map: Record<string, string> = {}
    for (const item of outbox.all()) map[item.chatId] = item.body
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outboxVersion, chats.length])

  /**
   * Черновики перечитываем при смене адреса: недописанное становится
   * видно в списке ровно тогда, когда человек из переписки вышел.
   */
  const where = useLocation()

  const drafts = useMemo(() => {
    const map: Record<string, string> = {}
    for (const chat of chats) {
      const saved = readDraft(chat.id)
      if (saved) map[chat.id] = saved
    }
    return map
  }, [chats, where.pathname])

  const waitingIn = (chatId: string): string | null => pendingByChat[chatId] ?? null

  const visible = showArchive ? chats.filter((c) => c.archived) : chats.filter((c) => !c.archived)

  const filtered = query.trim()
    ? visible.filter((c) => c.title.toLowerCase().includes(query.trim().toLowerCase()))
    : visible

  return (
    <aside className="sidebar">
      <header className="sidebar-head">
        <button
          className="icon-btn"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Меню"
          aria-expanded={menuOpen}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M3 6h18v2H3zM3 11h18v2H3zM3 16h18v2H3z" />
          </svg>
        </button>

        <input
          className="search"
          placeholder="Поиск"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <button
          className="icon-btn"
          onClick={() => setOpenSheet('new')}
          title="Новая переписка"
          aria-label="Новая переписка"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
          </svg>
        </button>
      </header>

      {menuOpen && (
        <div className="menu">
          <button
            className="menu-user"
            onClick={() => {
              setOpenSheet('profile')
              setMenuOpen(false)
            }}
          >
            {user && <Avatar id={user.id} name={user.displayName} url={user.avatarUrl} size={40} />}
            <div className="menu-user-text">
              <div className="menu-name">{user?.displayName}</div>
              <div className="menu-email">Изменить профиль</div>
            </div>
          </button>

          <button
            className="menu-item is-neutral"
            onClick={() => {
              setOpenSheet('family')
              setMenuOpen(false)
            }}
          >
            Семья и приглашения
          </button>
          <button
            className="menu-item is-neutral"
            onClick={() => {
              setNotificationsOpen(true)
              setMenuOpen(false)
            }}
          >
            Уведомления
          </button>
          {user?.isAdmin && (
            <Link to="/admin" className="menu-item is-neutral" onClick={() => setMenuOpen(false)}>
              Состояние сервера
            </Link>
          )}
          <button className="menu-item" onClick={logout}>
            Выйти
          </button>

          {/**
           * Когда собрана работающая сейчас версия.
           *
           * Установленное на телефон приложение может держать в кеше
           * вчерашнюю сборку: человек видит старое и считает, что
           * починки не случилось. Теперь это читается, а не гадается.
           */}
          <div className="build-stamp" title="Версия приложения">
            Версия от {buildDate()}
          </div>
        </div>
      )}

      {/**
       * Чаты и контакты — две вкладки одного списка, как в телеграме.
       *
       * Контакты не отдельный раздел приложения: это другой взгляд на
       * тех же людей. Переписки показывают, о чём говорили; контакты —
       * с кем можно заговорить, в том числе с тем, кому ещё ни разу
       * не писал.
       */}
      <PushBanner onOpenSettings={() => setNotificationsOpen(true)} />

      <div className="list-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'chats'}
          className={'list-tab' + (tab === 'chats' ? ' is-active' : '')}
          onClick={() => setTab('chats')}
        >
          Чаты
        </button>
        <button
          role="tab"
          aria-selected={tab === 'contacts'}
          className={'list-tab' + (tab === 'contacts' ? ' is-active' : '')}
          onClick={() => setTab('contacts')}
        >
          Контакты
        </button>
      </div>

      {tab === 'contacts' ? (
        <div className="chat-list scroll">
          <ContactList query={query} />
        </div>
      ) : (
      <>
      {archivedCount > 0 && !query && (
        <button className="archive-row" onClick={() => setShowArchive((v) => !v)}>
          {showArchive ? '← Обычные переписки' : `Архив · ${archivedCount}`}
        </button>
      )}

      <div className="chat-list scroll">
        {!loaded && (
          <div className="center-screen">
            <div className="spinner" />
          </div>
        )}

        {loaded && filtered.length === 0 && !query && (
          <p className="empty-hint">Пока нет переписок</p>
        )}

        {query.trim().length >= 2 && filtered.length > 0 && (
          <div className="search-group">Переписки</div>
        )}

        {filtered.map((chat) => (
          <NavLink
            key={chat.id}
            to={`/chat/${chat.id}`}
            className={({ isActive }) =>
              'chat-row' + (isActive ? ' is-active' : '') + (chat.pinned ? ' is-pinned' : '')
            }
            onContextMenu={(event) => {
              event.preventDefault()
              setRowMenu({ chat, x: event.clientX, y: event.clientY })
            }}
          >
            {chat.kind === 'SAVED' ? (
              <SavedIcon />
            ) : (
              <Avatar
                id={chat.id}
                name={chat.title}
                url={chat.avatarUrl}
                online={chat.kind === 'DIRECT' && chat.online}
              />
            )}

            <div className="chat-row-body">
              <div className="chat-row-top">
                <span className="chat-row-title">{chat.title}</span>
                {chat.pinned && <span className="chat-row-pin" aria-label="закреплено">📌</span>}
                {chat.muted && <span className="chat-row-mutedmark" aria-label="заглушено">🔕</span>}
                {chat.lastMessage && (
                  <span className="chat-row-time">{chatListTime(chat.lastMessage.createdAt)}</span>
                )}
              </div>

              <div className="chat-row-bottom">
                {/* Кто-то печатает — это важнее любого черновика:
                    ответ уже в пути, и человек ждёт именно его */}
                {typingIn(typing[chat.id], chat.kind) ? (
                  <span className="chat-row-preview is-typing">
                    {typingIn(typing[chat.id], chat.kind)}
                  </span>
                ) : (
                  <span className="chat-row-preview">
                    {!waitingIn(chat.id) && drafts[chat.id] && (
                      <span className="preview-draft">Черновик: </span>
                    )}
                    {waitingIn(chat.id) && (
                      <span className="preview-pending" title="ждёт связи">
                        ⏱{' '}
                      </span>
                    )}
                    {previewText(chat, user?.id, waitingIn(chat.id), drafts[chat.id] ?? null)}
                  </span>
                )}
                {/* Зов по имени видно даже в заглушённой переписке:
                    заглушают шум, а не обращение к себе */}
                {chat.mentioned && (
                  <span className="badge is-mention" title="Вас упомянули">
                    @
                  </span>
                )}
                {chat.unread > 0 && (
                  <span className={'badge' + (chat.muted ? ' is-muted' : '')}>{chat.unread}</span>
                )}
              </div>
            </div>
          </NavLink>
        ))}

        {query.trim().length >= 2 && (
          <>
            <div className="search-group">
              Сообщения
              {searching && <span className="search-spin" />}
            </div>

            {found?.length === 0 && !searching && (
              <p className="empty-hint">Ничего не нашлось</p>
            )}

            {found?.map((result) => (
              <NavLink
                key={result.id}
                to={`/chat/${result.chatId}?at=${result.id}`}
                className="chat-row"
                onClick={() => setQuery('')}
              >
                <Avatar
                  id={result.author?.id ?? result.chatId}
                  name={result.author?.displayName ?? '?'}
                  url={result.author?.avatarUrl ?? null}
                  size={40}
                />
                <div className="chat-row-body">
                  <div className="chat-row-top">
                    <span className="chat-row-title">
                      {result.chatKind === 'DIRECT'
                        ? (result.author?.displayName ?? 'Переписка')
                        : (result.chatTitle ?? 'Группа')}
                    </span>
                    <span className="chat-row-time">{messageTime(result.createdAt)}</span>
                  </div>
                  <div className="chat-row-bottom">
                    <span className="chat-row-preview">{result.body}</span>
                  </div>
                </div>
              </NavLink>
            ))}
          </>
        )}
      </div>
      </>
      )}
      {notificationsOpen && <NotificationSettings onClose={() => setNotificationsOpen(false)} />}
      {openSheet === 'profile' && <ProfileSettings onClose={() => setOpenSheet(null)} />}
      {openSheet === 'family' && <FamilyPanel onClose={() => setOpenSheet(null)} />}
      {openSheet === 'new' && <NewChatDialog onClose={() => setOpenSheet(null)} />}

      {rowMenu && (
        <div className="menu-overlay" onClick={() => setRowMenu(null)}>
          <div
            className="msg-menu"
            style={{ left: Math.min(rowMenu.x, window.innerWidth - 210), top: rowMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="msg-menu-item"
              onClick={() => {
                void pinChat(rowMenu.chat.id, !rowMenu.chat.pinned)
                setRowMenu(null)
              }}
            >
              {rowMenu.chat.pinned ? 'Открепить' : 'Закрепить наверху'}
            </button>
            <button
              className="msg-menu-item"
              onClick={() => {
                void muteChat(rowMenu.chat.id, rowMenu.chat.muted ? null : 8)
                setRowMenu(null)
              }}
            >
              {rowMenu.chat.muted ? 'Включить уведомления' : 'Заглушить на 8 часов'}
            </button>
            <button
              className="msg-menu-item"
              onClick={() => {
                void archiveChat(rowMenu.chat.id, !rowMenu.chat.archived)
                setRowMenu(null)
              }}
            >
              {rowMenu.chat.archived ? 'Вернуть из архива' : 'В архив'}
            </button>

            {/* «Прочитал, но ответить сейчас не могу» — иначе напоминание
                об этом остаётся в голове, а не в приложении */}
            {rowMenu.chat.unread === 0 && rowMenu.chat.lastMessage && (
              <button
                className="msg-menu-item"
                onClick={() => {
                  void markUnread(rowMenu.chat.id)
                  setRowMenu(null)
                }}
              >
                Отметить непрочитанным
              </button>
            )}

            <button
              className="msg-menu-item"
              onClick={() => {
                const chat = rowMenu.chat
                setRowMenu(null)
                if (
                  window.confirm(
                    `Очистить историю переписки «${chat.title}»?\n\n` +
                      'У собеседника она останется: это её половина, а не твоя.',
                  )
                ) {
                  void clearChat(chat.id)
                }
              }}
            >
              Очистить историю
            </button>

            {rowMenu.chat.kind !== 'SAVED' && (
              <button
                className="msg-menu-item is-danger"
                onClick={() => {
                  const chat = rowMenu.chat
                  const group = chat.kind === 'GROUP'
                  setRowMenu(null)

                  if (
                    window.confirm(
                      group
                        ? `Выйти из группы «${chat.title}»?\n\nОстальные увидят, что ты вышел.`
                        : `Убрать переписку с «${chat.title}»?\n\n` +
                            'Она вернётся, когда тебе напишут.',
                    )
                  ) {
                    void removeChat(chat.id)
                  }
                }}
              >
                {rowMenu.chat.kind === 'GROUP' ? 'Выйти из группы' : 'Убрать переписку'}
              </button>
            )}
          </div>
        </div>
      )}
    </aside>
  )
}

/**
 * Строчка под названием переписки.
 *
 * Три вещи, которых не хватало:
 *
 *   — недописанное показывается вместо последнего сообщения. Человек
 *     набрал половину и отвлёкся; без пометки он об этом не вспомнит,
 *     пока не откроет переписку заново;
 *   — своё сообщение подписано «Вы». Иначе в переписке вдвоём непонятно,
 *     последнее слово за тобой или за собеседником, и люди перечитывают
 *     переписку ради одного этого;
 *   — неотправленное важнее отправленного: если что-то висит в очереди,
 *     в списке должно быть видно именно оно.
 */
/**
 * «Печатает…» в строке списка.
 *
 * В группе без имени непонятно, кого ждать, а вдвоём имя лишнее —
 * там и так ясно кто. Отметки живут секунды и гаснут сами: сервер
 * присылает их с запасом по времени, а не с отдельным «перестал».
 */
function typingIn(
  entries: { displayName: string; until: number }[] | undefined,
  kind: ChatSummary['kind'],
): string | null {
  const live = (entries ?? []).filter((t) => t.until > Date.now())
  if (live.length === 0) return null
  if (kind === 'DIRECT') return 'печатает…'
  if (live.length === 1) return `${live[0]!.displayName} печатает…`
  return `${live.length} печатают…`
}

function previewText(
  chat: ChatSummary,
  meId: string | undefined,
  waiting: string | null,
  draft: string | null,
): string {
  if (waiting) return waiting
  if (draft) return draft

  const last = chat.lastMessage
  if (!last) return 'Нет сообщений'
  if (last.isSystem) return last.body ?? ''

  // Во вложении текста может не быть — тогда показываем, что это запись
  const text = last.body ?? '📎 Запись'

  if (last.authorId && meId && last.authorId === meId) return `Вы: ${text}`
  return chat.kind === 'GROUP' && last.authorName ? `${last.authorName}: ${text}` : text
}

/** Недописанное лежит там же, где его оставил набор сообщения. */
function readDraft(chatId: string): string | null {
  try {
    const saved = localStorage.getItem('family.draft.' + chatId)
    return saved && saved.trim() ? saved.trim() : null
  } catch {
    return null
  }
}
